import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import instance from '../apiInstance';
import './CompleteProfileModal.css';
import CompleteProfileFormActions from './CompleteProfileFormActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import countryCodes from "./countries/Countries";
import config from "../config";
import { appCopy } from "../pages/appCopy";
import { defaultDial, isSisterStore } from "../sisterStore";
import { isGateLeadOnly } from "../routes/isGateLeadOnly";
import {
  cadastroFormMissing,
  isCadastroFormComplete,
  isColetaComplete,
  isRealName,
  needsColetaModal,
  formatCadastroPhone,
} from "../coletaProfile";
import { trackClient } from "../trackerBus";

/** Home: poucos segundos para ver conteúdo; modal abre logo (cadastro não pode “sumir”). */
export const COLETA_HOME_DELAY_MS = 4_000;
const COLETA_SOFT_PATHS = new Set(["/home", "/news"]);

const brandOptions = [
  "Louis Vuitton", "Gucci", "Chanel", "Hermes",
  "Dior", "Prada", "Versace", "Balenciaga",
  "Fendi", "YSL", "Valentino", "Dolce Gabbana",
  "Bottega", "Celine", "Chloe", "MiuMiu", "Goyard",
];

const CompleteProfileModal = () => {
  const t = appCopy(config.STORE_LOCALE);
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState(defaultDial());
  const usPhone = isSisterStore();
  const [ddd, setDdd] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [insta, setInsta] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [date, setDate] = useState("");
  const [gender, setGender] = useState("");
  const [produtoPromo, setProdutoPromo] = useState("");

  const [surveyConfig, setSurveyConfig] = useState(null);
  const [legacyLead, setLegacyLead] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const fieldRefs = React.useRef({});

  const formSnapshot = React.useMemo(() => ({
    name,
    countryCode,
    ddd,
    phoneNumber,
    usPhone,
    gender,
    selectedBrands,
    selectedOption,
    produtoPromo,
    legacyLead,
    insta,
    date,
  }), [
    name, countryCode, ddd, phoneNumber, usPhone, gender,
    selectedBrands, selectedOption, produtoPromo, legacyLead, insta, date,
  ]);

  const missingFields = React.useMemo(
    () => cadastroFormMissing(formSnapshot),
    [formSnapshot],
  );

  const formComplete = missingFields.length === 0;

  const scrollToField = (key) => {
    const el = fieldRefs.current[key];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const hydrateFromUser = React.useCallback((u) => {
    if (!u) return;
    const prefs = u.preferences || {};
    const leadOnly = isGateLeadOnly(u);
    setLegacyLead(leadOnly);

    if (!needsColetaModal(u) && !leadOnly) {
      setIsOpen(false);
      window.dispatchEvent(new Event("cadastro_completed"));
      return;
    }

    setIsOpen(true);
    setEmail(u.email || "");
    setName(isRealName(u.name) ? u.name : "");
    setCountryCode(defaultDial());
    if (u.phone && u.phone !== "indefinido") {
      if (u.phone.startsWith("+55") && u.phone.length >= 13) {
        setDdd(u.phone.substring(3, 5));
        setPhoneNumber(u.phone.substring(5));
      } else if (u.phone.startsWith("+")) {
        const match = u.phone.match(/^(\+\d{1,3})(.+)$/);
        if (match) {
          setCountryCode(match[1]);
          setPhoneNumber(match[2]);
        } else {
          setPhoneNumber(u.phone);
        }
      } else {
        setPhoneNumber(u.phone);
      }
    } else {
      setDdd("");
      setPhoneNumber("");
    }
    if (u.birth_date) {
      const raw = String(u.birth_date);
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const [y, m, d] = raw.slice(0, 10).split("-");
        setDate(`${d}/${m}/${y}`);
      } else {
        setDate(raw);
      }
    } else {
      setDate("");
    }
    setGender(u.gender || "");
    setInsta(u.instagram_handle || "");
    setSelectedBrands(prefs.selected_brands || prefs.favorite_brands || []);
    setSelectedOption(prefs.selected_option || "");
    setProdutoPromo(prefs.produto_promo || "");
  }, []);

  const scheduleColetaOpen = React.useCallback((user, { delayed = false } = {}) => {
    if (!user || !needsColetaModal(user)) {
      setIsOpen(false);
      window.dispatchEvent(new Event("cadastro_completed"));
      return () => {};
    }
    const delayMs = delayed && COLETA_SOFT_PATHS.has(location.pathname)
      ? COLETA_HOME_DELAY_MS
      : 0;
    const run = () => {
      hydrateFromUser(user);
      setIsOpen(true);
    };
    if (delayMs <= 0) {
      run();
      return () => {};
    }
    const timer = window.setTimeout(run, delayMs);
    return () => window.clearTimeout(timer);
  }, [hydrateFromUser, location.pathname]);

  useEffect(() => {
    if (!token) return undefined;
    if (location.pathname === "/" || location.pathname === "/register" || location.pathname === "/gate") {
      return undefined;
    }

    let cancelled = false;
    let clearTimer = () => {};

    instance.get("/surveys/cadastro-incompleto")
      .then((res) => {
        if (!cancelled && res.data) setSurveyConfig(res.data);
      })
      .catch((err) => console.error("Error fetching survey config:", err));

    instance.get("/me")
      .then((res) => {
        if (cancelled) return;
        const user = res.data?.user;
        const delayed = COLETA_SOFT_PATHS.has(location.pathname);
        clearTimer = scheduleColetaOpen(user, { delayed }) || (() => {});
      })
      .catch((err) => {
        console.error("Error fetching user for CompleteProfileModal:", err);
        if (!cancelled) setIsOpen(true);
      });

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [token, location.pathname, scheduleColetaOpen]);

  useEffect(() => {
    if (!token) return undefined;
    let clearTimer = () => {};
    const onGateRequired = (ev) => {
      clearTimer();
      clearTimer = scheduleColetaOpen(ev.detail?.user, { delayed: Boolean(ev.detail?.soft) }) || (() => {});
    };
    window.addEventListener("coleta_gate_required", onGateRequired);
    return () => {
      window.removeEventListener("coleta_gate_required", onGateRequired);
      clearTimer();
    };
  }, [token, scheduleColetaOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const blockEscape = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", blockEscape, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", blockEscape, true);
    };
  }, [isOpen]);

  useEffect(() => {
    window.dispatchEvent(new Event(isOpen ? "cadastro_modal_open" : "cadastro_modal_closed"));
  }, [isOpen]);

  const formatDate = (input) => {
    const c = input.replace(/\D/g, "");
    return `${c.slice(0, 2)}/${c.slice(2, 4)}/${c.slice(4, 8)}`;
  };

  /** UI = DD/MM/AAAA → API/Supabase = AAAA-MM-DD (igual ao register). */
  const toIsoBirthDate = (brDate) => {
    if (!brDate?.trim()) return null;
    const parts = brDate.trim().split("/");
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    if (dd.length !== 2 || mm.length !== 2 || yyyy.length !== 4) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    if (!isCadastroFormComplete(formSnapshot)) {
      const labels = missingFields.map((m) => m.label);
      setError(`Falta: ${labels.join(", ")}`);
      scrollToField(missingFields[0]?.key);
      return;
    }

    const birthIso = date.trim() ? toIsoBirthDate(date) : null;
    if (date.trim() && !birthIso) {
      setError(t.profileErr);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const phone = formatCadastroPhone(countryCode, ddd, phoneNumber, usPhone);
      // First update profile
      const profileRes = await instance.put('/profile', {
        name: name.trim(),
        phone,
        birth_date: birthIso,
        gender: gender || null,
        instagram_handle: insta.trim() || null,
        preferences: {
          selected_brands: selectedBrands,
          favorite_brands: selectedBrands,
          selected_option: selectedOption,
          produto_promo: produtoPromo.trim(),
          force_coleta_modal: false,
          coleta_completed_at: new Date().toISOString(),
        }
      });

      const savedUser = profileRes.data?.user;
      if (savedUser && !isColetaComplete(savedUser)) {
        setError(t.needFullForm);
        setShowValidation(true);
        return;
      }

      trackClient("coleta_completed", {
        metadata: {
          brands_count: selectedBrands.length,
          has_insta: Boolean(insta.trim()),
          has_birthday: Boolean(birthIso),
        },
      });

      // Then submit survey to get points if config exists
      // Survey inativa/quebrada NÃO deve bloquear o cadastro já salvo
      if (surveyConfig?.id) {
        try {
          await instance.post(`/surveys/${surveyConfig.id}/submit`, {
            answers: {
              name,
              phone,
              birth_date: birthIso,
              instagram_handle: insta || null,
              gender: gender || null,
              selected_brands: selectedBrands,
              selected_option: selectedOption,
              produto_promo: produtoPromo
            }
          });
          
          if (surveyConfig.reward_points > 0) {
            alert(t.profileCongrats(surveyConfig.reward_points));
          }
        } catch (surveyErr) {
          console.warn('Survey submit failed (profile already saved):', surveyErr);
        }
      }

      setShowValidation(false);
      setIsOpen(false);
      window.dispatchEvent(new Event("cadastro_completed"));
    } catch (err) {
      console.error('Error updating profile:', err);
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t.profileErr,
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="complete-profile-overlay">
      <div className="complete-profile-modal">
        <div className="complete-profile-header">
          <FontAwesomeIcon icon={faUserEdit} size="3x" color="#D4AF37" style={{ marginBottom: 16 }} />
          <h2>{surveyConfig ? surveyConfig.title : t.profileTitle}</h2>
          <p>{surveyConfig ? surveyConfig.description : t.profileBody}</p>
          
          {surveyConfig && surveyConfig.reward_points > 0 ? (
            <div className="complete-profile-points-badge">
              <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
              {t.earnPointsComplete(surveyConfig.reward_points)}
            </div>
          ) : (
            <div className="complete-profile-points-badge">
              <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
              {t.earnPointsComplete(100)}
            </div>
          )}
        </div>

        <form className="complete-profile-form" onSubmit={handleSubmit}>
          <div className="complete-profile-form-body">
          {/* Nome */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "name") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.name = el; }}
          >
            <label>{t.nameLabel} *</label>
            <input 
              type="text" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder={t.namePlaceholder}
              required 
            />
          </div>

          {/* Email — já cadastrado, só confirma */}
          <div className="complete-profile-input-group">
            <label>{t.emailLabel}</label>
            <input
              type="email"
              value={email}
              readOnly
              className="complete-profile-input-readonly"
              placeholder={t.emailPlaceholder}
            />
          </div>

          <div className="complete-profile-divider">{t.contact}</div>

          {/* WhatsApp — igual cadastro: bandeira + DDI + DDD + número */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "phone") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.phone = el; }}
          >
            <label>{t.whatsapp} *</label>
            <span className="complete-profile-field-hint">DDD + número (ex: 48 99999-9999)</span>
            <div className="complete-profile-phone-row" style={usPhone ? { gridTemplateColumns: "0.8fr 1.6fr" } : undefined}>
              <select
                className="complete-profile-select"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                aria-label="País"
              >
                {Object.keys(countryCodes).map((code) => (
                  <option key={code} value={code}>
                    {countryCodes[code]} {code}
                  </option>
                ))}
              </select>
              {!usPhone && (
              <input
                type="text"
                inputMode="numeric"
                placeholder={t.ddd}
                value={ddd}
                onChange={(e) => setDdd(e.target.value.replace(/\D/g, '').slice(0, 2))}
                required
              />
              )}
              <input
                type="text"
                inputMode="numeric"
                placeholder={t.phonePlaceholder}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, usPhone ? 10 : 9))}
                required
              />
            </div>
          </div>

          {/* Instagram */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "insta") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.insta = el; }}
          >
            <label>{t.instaLabel}{legacyLead ? " *" : " (opcional)"}</label>
            <input
              type="text"
              placeholder={t.instaPlaceholder}
              value={insta}
              onChange={(e) => setInsta(e.target.value)}
            />
          </div>

          <div className="complete-profile-divider">{t.prefs}</div>

          {/* Marcas — chips (dropdown Bootstrap quebrava no iPhone) */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "brands") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.brands = el; }}
          >
            <label>{t.brandsLabel} *{legacyLead ? " (mín. 3)" : ""}</label>
            <span className="complete-profile-field-hint">Toque para marcar — pode escolher várias</span>
            <div className="complete-profile-radio-group">
              {brandOptions.map((brand) => {
                const selected = selectedBrands.includes(brand);
                return (
                  <div className="complete-profile-radio-option" key={brand}>
                    <input
                      type="checkbox"
                      id={`brand-${brand}`}
                      checked={selected}
                      onChange={() => handleBrandChange(brand)}
                    />
                    <label className="complete-profile-radio-label" htmlFor={`brand-${brand}`}>
                      {brand}
                    </label>
                  </div>
                );
              })}
            </div>
            {selectedBrands.length > 0 && (
              <p className="complete-profile-selected-brands">
                {t.selected}: {selectedBrands.join(", ")}
              </p>
            )}
          </div>

          {/* Bolsa de griffe */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "bolsa") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.bolsa = el; }}
          >
            <label>{t.bagLabel} *</label>
            <div className="complete-profile-radio-group">
              {[
                { value: "Ainda não", label: t.bagNone },
                { value: "Tenho uma", label: t.bagOne },
                { value: "Tenho várias, amo!", label: t.bagMany },
              ].map((opt) => (
                <div className="complete-profile-radio-option" key={opt.value}>
                  <input
                    type="radio"
                    name="bolsa"
                    id={`bolsa-${opt.value}`}
                    value={opt.value}
                    checked={selectedOption === opt.value}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <label className="complete-profile-radio-label" htmlFor={`bolsa-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Aniversário */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "birthday") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.birthday = el; }}
          >
            <label>{t.birthday}{legacyLead ? " *" : " (opcional)"}</label>
            <input
              type="text"
              placeholder={t.datePlaceholder}
              value={date}
              onChange={(e) => setDate(formatDate(e.target.value))}
            />
          </div>

          {/* Gênero */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "gender") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.gender = el; }}
          >
            <label>{t.gender} *</label>
            <div className="complete-profile-radio-group">
              {[
                { value: "feminino", label: t.female },
                { value: "masculino", label: t.male },
              ].map((opt) => (
                <div className="complete-profile-radio-option" key={opt.value}>
                  <input
                    type="radio"
                    name="genero"
                    id={`genero-${opt.value}`}
                    value={opt.value}
                    checked={gender === opt.value}
                    onChange={(e) => setGender(e.target.value)}
                  />
                  <label className="complete-profile-radio-label" htmlFor={`genero-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Produto promo */}
          <div
            className={`complete-profile-input-group${showValidation && missingFields.some((m) => m.key === "produto_promo") ? " complete-profile-input-group--invalid" : ""}`}
            ref={(el) => { fieldRefs.current.produto_promo = el; }}
          >
            <label>{t.promoLabel} *</label>
            <input
              type="text"
              placeholder={t.promoPlaceholder}
              value={produtoPromo}
              onChange={(e) => setProdutoPromo(e.target.value)}
              required
            />
          </div>
          </div>
          <CompleteProfileFormActions
            error={error}
            hint="Toque em Salvar — o botão fica fixo aqui embaixo"
            showHint={!formComplete && !showValidation}
            missingLabels={showValidation ? missingFields.map((m) => m.label) : []}
            loading={loading}
            canSubmit={formComplete}
            loadingLabel={t.saving}
            submitLabel={formComplete ? t.reconfirm : "Salvar cadastro"}
          />
        </form>
      </div>
    </div>
  );
};

export default CompleteProfileModal;
